import { MapPin, X, Plus, MousePointerClick } from 'lucide-react';

/**
 * LocationDropPinCard — Google Maps-style info card displayed below the address
 * search bar after a location is resolved. Lists the formatted address + GPS
 * coordinates + a primary CTA to create a project at this location.
 *
 * The card is fully glass-morphism (matches HUD aesthetic) and includes a hint
 * to click on the map for fine-tuning the pin position.
 *
 * Props:
 *  - pin: { lat, lon, display_name, source? }  (null hides the card)
 *  - onCreateProject: () => void   (opens NewProjectWizard with prefilled address)
 *  - onClear: () => void           (removes the dropped pin)
 */
const LocationDropPinCard = ({ pin, onCreateProject, onClear }) => {
  if (!pin) return null;

  const title = (pin.display_name || '').split(',')[0]?.trim() || 'Position sélectionnée';
  const subtitle = (pin.display_name || '').split(',').slice(1, 4).join(',').trim();

  return (
    <div
      className="rounded-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
      style={{
        background: 'rgba(10,10,13,0.92)',
        border: '1px solid rgba(217,160,91,0.35)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px rgba(217,160,91,0.08)',
      }}
      data-testid="location-droppin-card"
    >
      <div className="flex items-start gap-2.5 p-3">
        <div
          className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
          style={{
            background: 'rgba(217,160,91,0.18)',
            border: '1px solid rgba(217,160,91,0.32)',
          }}
        >
          <MapPin className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[13px] font-semibold leading-tight truncate"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)' }}
          >
            {title}
          </p>
          {subtitle && (
            <p className="text-[11px] mt-0.5 truncate" style={{ color: 'var(--c-text-muted)' }}>
              {subtitle}
            </p>
          )}
          <p
            className="hud-tabular text-[10.5px] mt-1 tracking-wide"
            style={{ color: 'var(--c-text-faint)' }}
            data-testid="location-droppin-coords"
          >
            {Number(pin.lat).toFixed(5)}, {Number(pin.lon).toFixed(5)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-white/40 hover:text-white/85 transition-colors shrink-0 mt-0.5"
          aria-label="Effacer la position"
          data-testid="location-droppin-clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Hint: drag-to-reposition style cue */}
      <div
        className="px-3 py-1.5 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold"
        style={{
          color: 'rgba(217,160,91,0.85)',
          background: 'rgba(217,160,91,0.04)',
          borderTop: '1px solid rgba(217,160,91,0.10)',
        }}
      >
        <MousePointerClick className="w-3 h-3" />
        Clique sur la carte pour ajuster
      </div>

      {/* Primary CTA */}
      <button
        type="button"
        onClick={onCreateProject}
        className="w-full px-3 py-2.5 text-[12.5px] font-semibold flex items-center justify-center gap-2 transition-colors"
        style={{
          background: 'var(--c-gold)',
          color: '#0E0E10',
          borderTop: '1px solid rgba(0,0,0,0.18)',
        }}
        data-testid="location-droppin-create"
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
        Créer un projet à cette adresse
      </button>
    </div>
  );
};

export default LocationDropPinCard;
